import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api"; // ✅ shared API
import "../global.css";
import bgTaxi from "../assets/bg-taxi.png";

const Driver = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    licence_status: "",
    licence_authority: "",
    license_number: "",
    license_expiry: "",
    vehicle: {
      make: "",
      model: "",
      type: "",
      reg: "",
      insurance_number: "",
      insurance_expiry: "",
      max_passengers: "",
      max_bags: "",
    },
  });

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // ✅ Send verification code
  const sendCode = async () => {
    try {
      const res = await api.post("/api/driver/send-code", { email });
      setMessage(res.data.message);
      setCooldown(120);
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to send code");
    }
  };

  // ✅ Verify code
  const verifyCode = async () => {
    try {
      const res = await api.post("/api/driver/verify-code", { email, code });
      if (res.data.verified) {
        setStep(2);
        setMessage("✅ Email verified! Please complete your details.");
      }
    } catch (err) {
      setMessage(err.response?.data?.error || "Verification failed");
    }
  };

  // ✅ Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("vehicle.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        vehicle: { ...prev.vehicle, [key]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ✅ Submit registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const vehiclePayload = {
        type: formData.vehicle.type,
        max_passengers: formData.vehicle.max_passengers || 4,
        max_bags: formData.vehicle.max_bags || 3,
        registration_number: formData.vehicle.reg,
      };

      const res = await api.post("/api/driver/register", {
        ...formData,
        email,
        vehicles: [vehiclePayload],
      });

      if (res.data?.ref_code) {
        navigate("/driver-success", { state: { ref_code: res.data.ref_code } });
      } else {
        navigate("/driver-success");
      }
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to register");
    }
  };

  return (
    <div
      className="hero-section"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${bgTaxi})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "650px",
          width: "100%",
          margin: "auto",
          padding: "2rem",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "1rem" }}>🚖 Driver Registration</h2>
        {message && <p style={{ textAlign: "center", color: "green" }}>{message}</p>}

        {/* STEP 1: Email Verification */}
        {step === 1 && (
          <div style={{ textAlign: "center" }}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
            />
            <button
              onClick={sendCode}
              disabled={cooldown > 0}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "12px",
                background: cooldown > 0 ? "#ccc" : "#ffc107",
                border: "none",
                borderRadius: "6px",
                cursor: cooldown > 0 ? "not-allowed" : "pointer",
                fontWeight: "bold",
              }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Send Code"}
            </button>

            <input
              type="text"
              placeholder="Enter verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
            />
            <button
              onClick={verifyCode}
              style={{
                width: "100%",
                padding: "12px",
                background: "#28a745",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
              }}
            >
              Verify Code
            </button>
          </div>
        )}

        {/* STEP 2: Registration Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            {/* Driver Info */}
            {[
              ["name", "Full Name"],
              ["phone", "Phone (UK only)"],
              ["address", "Address"],
              ["licence_authority", "Licence Authority (if licensed)"],
              ["license_number", "Licence Number"],
            ].map(([key, placeholder]) => (
              <input
                key={key}
                type="text"
                name={key}
                placeholder={placeholder}
                value={formData[key]}
                onChange={handleChange}
                style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
                required={key !== "licence_authority" && key !== "license_number"}
              />
            ))}

            <select
              name="licence_status"
              value={formData.licence_status}
              onChange={handleChange}
              style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
              required
            >
              <option value="">Select Licence Status</option>
              <option value="licensed">Licensed</option>
              <option value="pending">Pending</option>
              <option value="none">Not Licensed</option>
            </select>

            <input
              type="date"
              name="license_expiry"
              value={formData.license_expiry}
              onChange={handleChange}
              style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
            />

            <h4>Vehicle Details</h4>
            {[
              ["vehicle.make", "Car Make"],
              ["vehicle.model", "Car Model"],
              ["vehicle.type", "Car Type (Saloon, MPV, etc.)"],
              ["vehicle.reg", "Car Registration"],
            ].map(([name, placeholder]) => (
              <input
                key={name}
                type="text"
                name={name}
                placeholder={placeholder}
                value={name.split(".").reduce((obj, key) => obj[key], formData)}
                onChange={handleChange}
                style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
              />
            ))}

            <button
              type="submit"
              style={{
                width: "100%",
                padding: "12px",
                background: "#ffc107",
                fontWeight: "600",
                border: "none",
                borderRadius: "6px",
              }}
            >
              Submit Registration
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Driver;

